import { useState, useEffect } from 'react';
import { X, Download } from '@phosphor-icons/react';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(standalone);

    // Check if iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Listen for beforeinstallprompt event
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after 3 seconds (only if not installed)
      setTimeout(() => {
        if (!standalone) {
          setShowPrompt(true);
        }
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('✅ User accepted PWA install');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Don't show again for this session
    sessionStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  // Don't show if already installed or user dismissed
  if (isStandalone || sessionStorage.getItem('pwa-prompt-dismissed')) {
    return null;
  }

  // iOS Install Instructions
  if (isIOS && showPrompt) {
    return (
      <div 
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm text-white rounded-2xl shadow-2xl p-5 z-50 animate-slide-up"
        style={{ backgroundColor: '#7e22ce' }}
      >
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-white/80 hover:text-white"
        >
          <X size={22} weight="bold" />
        </button>
        
        <div className="flex items-start gap-4">
          <div className="bg-white/20 p-3 rounded-2xl flex-shrink-0">
            <Download size={28} weight="bold" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-xl mb-1 text-white">Install DWS Vault Chat</h3>
            <p className="text-sm text-white/90 mb-4 leading-relaxed">
              Access faster with our app! Install now for:
            </p>
            <div className="text-xs text-white/95 bg-white/10 rounded-xl p-3 space-y-1.5 border border-white/10">
              <p className="font-semibold mb-1">iOS Instructions:</p>
              <p>📱 Tap Share button below</p>
              <p>➕ Then "Add to Home Screen"</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Android/Desktop Install Prompt
  if (deferredPrompt && showPrompt) {
    return (
      <div 
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm text-white rounded-2xl shadow-2xl p-5 z-50 animate-slide-up"
        style={{ backgroundColor: '#7e22ce' }}
      >
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-white/80 hover:text-white"
        >
          <X size={22} weight="bold" />
        </button>
        
        <div className="flex items-start gap-4">
          <div className="bg-white/20 p-3 rounded-2xl flex-shrink-0">
            <Download size={28} weight="bold" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-xl mb-1 text-white">Install DWS Vault Chat</h3>
            <p className="text-sm text-white/90 mb-3 leading-relaxed">
              Access faster with our app! Install now for:
            </p>
            <ul className="text-sm text-white/90 space-y-1.5 mb-5 font-medium">
              <li className="flex items-center gap-1.5">✅ Full screen experience</li>
              <li className="flex items-center gap-1.5">✅ Works offline</li>
              <li className="flex items-center gap-1.5">✅ Faster loading</li>
              <li className="flex items-center gap-1.5">✅ Home screen access</li>
            </ul>
            <div className="flex items-center gap-4">
              <button
                onClick={handleInstall}
                className="flex-1 bg-white text-purple-700 font-bold py-3 px-5 rounded-2xl hover:bg-gray-100 transition-colors shadow-sm text-center"
              >
                Install Now
              </button>
              <button
                onClick={handleDismiss}
                className="text-white/80 hover:text-white font-bold text-sm"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}




export default PWAInstallPrompt;
